import { notifyNativeWheelRefresh } from "./pageBridge";
import { normalizeDateInput, prepareExpDateField } from "./datePicker";
import {
  dualWriteAssignmentPairLedger,
  dualWriteBatchDeleteLedgerTransactions,
  dualWriteCreateLedgerTransaction,
  dualWriteResolveLedgerPosition,
  dualWriteUpdateLedgerTransaction,
  dualWriteUpdateLedgerTransactionByOwnId,
  loadOwnLedgerHistoryBundle,
} from "./ownLedgerClient";
import { loadOwnLedgerFeatures } from "./ownLedgerFeatures";
import {
  fmtNum,
  fmtUsd,
  invalidateWheelSnapshots,
  type OptionPosition,
  type WheelRecommendation,
} from "./watchlistData";

export { normalizeDateInput, prepareExpDateField };

/** Robinhood reports total contract dollars (negative for short); ledger uses per-share. */
export function normalizeRhOptionPremium(
  rawPrice: number | null | undefined,
  multiplier = 100,
): number | null {
  if (rawPrice == null || !Number.isFinite(rawPrice)) return null;
  const mult = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 100;
  return Math.round((Math.abs(rawPrice) / mult) * 10000) / 10000;
}

/** Drop binary noise so idempotency keys stay within Stock Forge's 64-char limit. */
function roundOptionPremium(value: number): number {
  return Math.round(value * 10000) / 10000;
}

const WHEEL_API_BASE = "/api/v1/wheel";

export type PositionResolution = "closed" | "expired" | "assigned";

export interface WheelLedgerTransaction {
  id: number;
  sf_transaction_id?: number | null;
  instrument_type?: string;
  action?: string;
  side?: string;
  ticker?: string;
  trade_date?: string;
  quantity?: number;
  avg_price?: number;
  strike?: number;
  exp_date?: string;
  option_symbol?: string;
  close_reason?: string;
  linked_transaction_id?: number | null;
  wheel_pair_linked_ids?: number[];
}

export class WheelActionError extends Error {
  code?: string | null;

  constructor(message: string, code?: string | null) {
    super(message);
    this.name = "WheelActionError";
    this.code = code ?? null;
  }
}

function etTodayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function apiErrMsg(result: { error?: string | null; code?: string | null }): string {
  return result.error?.trim() || "Request failed";
}

async function wheelGet<T>(path: string): Promise<T> {
  const fullPath = `${WHEEL_API_BASE}${path}`;
  const pageApi = (window as unknown as { API?: { get: (p: string, o?: object) => Promise<T> } }).API;
  if (pageApi?.get) {
    try {
      return await pageApi.get(fullPath, { silent: true });
    } catch (err) {
      throw new WheelActionError(err instanceof Error ? err.message : String(err));
    }
  }
  const res = await fetch(fullPath, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (res.ok) return res.json() as Promise<T>;
  throw new WheelActionError(`${path}: fetch failed`);
}

async function wheelMutate<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T | null> {
  const fullPath = `${WHEEL_API_BASE}${path}`;
  const m = method.toUpperCase();

  const res = await fetch(fullPath, {
    method: m,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body != null ? { "Content-Type": "application/json" } : {}),
    },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("json")
    ? await res.json().catch(() => null)
    : null;
  if (res.ok) return payload as T;
  const detail =
    payload && typeof payload === "object"
      ? ((payload as { detail?: { message?: string; code?: string } }).detail ?? payload)
      : null;
  throw new WheelActionError(
    (detail && typeof detail === "object" && "message" in detail
      ? String(detail.message)
      : null) ||
      apiErrMsg((payload as { error?: string; code?: string }) ?? {}),
    detail && typeof detail === "object" && "code" in detail
      ? String(detail.code)
      : (payload as { code?: string })?.code,
  );
}

/** POST /transactions — routes through own-ledger dual-write when enabled. */
export async function postLedgerTransaction(body: Record<string, unknown>): Promise<unknown | null> {
  const features = await loadOwnLedgerFeatures();
  if (features.enabled && features.dualWrite) {
    try {
      return await dualWriteCreateLedgerTransaction(body);
    } catch (err) {
      throw new WheelActionError(
        err instanceof Error ? err.message : "Dual-write failed",
        err && typeof err === "object" && "code" in err ? String((err as { code?: string }).code) : null,
      );
    }
  }
  return wheelMutate("POST", "/transactions", body);
}

async function isOwnLedgerDualWriteEnabled(): Promise<boolean> {
  const features = await loadOwnLedgerFeatures();
  return features.enabled && features.dualWrite;
}

async function fetchTransactionsForEdits(): Promise<WheelLedgerTransaction[]> {
  if (await isOwnLedgerDualWriteEnabled()) {
    const bundle = await loadOwnLedgerHistoryBundle(true);
    return bundle.transactions
      .map((txn) => normalizeLedgerTxn(txn as unknown as Record<string, unknown>))
      .filter((t) => Number.isFinite(t.id));
  }
  return fetchLedgerTransactions();
}

function wrapDualWriteError(err: unknown): never {
  throw new WheelActionError(
    err instanceof Error ? err.message : "Dual-write failed",
    err && typeof err === "object" && "code" in err ? String((err as { code?: string }).code) : null,
  );
}

export function buildOccSymbol(
  ticker: string,
  expiration: string,
  side: string,
  strike: number,
): string | null {
  const root = String(ticker || "")
    .trim()
    .toUpperCase();
  const parts = String(expiration || "").split("-");
  if (parts.length !== 3) return null;
  const [y, mo, d] = parts.map((x) => parseInt(x, 10));
  if (!y || !mo || !d) return null;
  const exp =
    String(y % 100).padStart(2, "0") +
    String(mo).padStart(2, "0") +
    String(d).padStart(2, "0");
  const t = String(side || "")
    .trim()
    .toLowerCase()
    .charAt(0) === "c"
    ? "C"
    : "P";
  const mills = Math.round(Number(strike) * 1000);
  if (!root || !(mills > 0)) return null;
  return root + exp + t + String(mills).padStart(8, "0");
}

function normalizeLedgerTxn(raw: Record<string, unknown>): WheelLedgerTransaction {
  return {
    id: Number(raw.id),
    sf_transaction_id:
      raw.sf_transaction_id != null && Number(raw.sf_transaction_id) > 0
        ? Number(raw.sf_transaction_id)
        : null,
    instrument_type:
      typeof raw.instrument_type === "string" ? raw.instrument_type : undefined,
    action: typeof raw.action === "string" ? raw.action : undefined,
    side: typeof raw.side === "string" ? raw.side : undefined,
    ticker: typeof raw.ticker === "string" ? raw.ticker : undefined,
    trade_date: typeof raw.trade_date === "string" ? raw.trade_date : undefined,
    quantity: raw.quantity != null ? Number(raw.quantity) : undefined,
    avg_price: raw.avg_price != null ? Number(raw.avg_price) : undefined,
    strike: raw.strike != null ? Number(raw.strike) : undefined,
    exp_date: typeof raw.exp_date === "string" ? raw.exp_date : undefined,
    option_symbol: typeof raw.option_symbol === "string" ? raw.option_symbol : undefined,
    close_reason: typeof raw.close_reason === "string" ? raw.close_reason : undefined,
    linked_transaction_id:
      raw.linked_transaction_id != null ? Number(raw.linked_transaction_id) : null,
    wheel_pair_linked_ids: Array.isArray(raw.wheel_pair_linked_ids)
      ? raw.wheel_pair_linked_ids
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
      : undefined,
  };
}

const LEDGER_TXN_PAGE_LIMIT = 500;
const LEDGER_TXN_MAX_PAGES = 40;

export async function fetchLedgerTransactions(limit = 2000): Promise<WheelLedgerTransaction[]> {
  const data = await wheelGet<{ transactions?: Record<string, unknown>[] }>(
    `/transactions?limit=${limit}`,
  );
  return (data.transactions ?? [])
    .map(normalizeLedgerTxn)
    .filter((t) => Number.isFinite(t.id));
}

/** Paginated fetch — matches server agentic ledger matching depth. */
export async function fetchAllLedgerTransactions(): Promise<WheelLedgerTransaction[]> {
  const txns: WheelLedgerTransaction[] = [];
  let offset = 0;
  for (let page = 0; page < LEDGER_TXN_MAX_PAGES; page += 1) {
    const data = await wheelGet<{ transactions?: Record<string, unknown>[] }>(
      `/transactions?limit=${LEDGER_TXN_PAGE_LIMIT}&offset=${offset}`,
    );
    const batch = (data.transactions ?? [])
      .map(normalizeLedgerTxn)
      .filter((t) => Number.isFinite(t.id));
    txns.push(...batch);
    if (batch.length < LEDGER_TXN_PAGE_LIMIT) break;
    offset += LEDGER_TXN_PAGE_LIMIT;
  }
  return txns;
}

export async function findOpeningTransaction(
  position: OptionPosition,
): Promise<WheelLedgerTransaction | null> {
  if (!position.option_symbol) return null;
  const all = await fetchLedgerTransactions();
  const matches = all.filter(
    (t) =>
      t.option_symbol === position.option_symbol &&
      t.instrument_type === "option" &&
      t.action === "sell" &&
      !t.close_reason,
  );
  if (!matches.length) return null;
  matches.sort((a, b) => String(a.trade_date).localeCompare(String(b.trade_date)));
  return matches[0] ?? null;
}

export function positionSummary(position: OptionPosition): string {
  return (
    `${position.ticker} ${(position.side || "").toUpperCase()} ${fmtNum(position.strike, 2)}` +
    ` exp ${position.exp_date ?? "—"} · ${fmtNum(position.contracts, 0)} open`
  );
}

export async function resolveOpenPosition(
  position: OptionPosition,
  resolution: PositionResolution,
  input: { tradeDate: string; quantity?: number; avgPrice?: number },
): Promise<void> {
  if (!position.option_symbol) throw new WheelActionError("Missing option symbol.");
  const body: Record<string, unknown> = {
    resolution,
    trade_date: input.tradeDate,
  };
  if (input.quantity != null && input.quantity > 0) body.quantity = input.quantity;
  if (resolution === "closed") {
    if (input.avgPrice == null || !(input.avgPrice >= 0)) {
      throw new WheelActionError("Enter the buy-to-close premium per share.");
    }
    body.avg_price = input.avgPrice;
  }
  body.idempotency_key = [
    "resolve",
    position.option_symbol,
    resolution,
    input.tradeDate,
    input.quantity ?? "",
  ].join(":");

  if (await isOwnLedgerDualWriteEnabled()) {
    try {
      await dualWriteResolveLedgerPosition(position.option_symbol, body);
      return;
    } catch (err) {
      wrapDualWriteError(err);
    }
  }
  await wheelMutate(
    "POST",
    `/positions/${encodeURIComponent(position.option_symbol)}/resolve`,
    body,
  );
}

export async function deleteOpenPosition(position: OptionPosition): Promise<number> {
  if (!position.option_symbol) throw new WheelActionError("Missing option symbol.");
  const sym = position.option_symbol;
  const all = await fetchLedgerTransactions();
  const optionTxns = all.filter((t) => t.option_symbol === sym);
  if (!optionTxns.length) {
    throw new WheelActionError("No transactions found for this contract.");
  }

  const byId = new Map<number, WheelLedgerTransaction>();
  all.forEach((t) => byId.set(t.id, t));

  const ids = new Set(optionTxns.map((t) => t.id));
  ids.forEach((id) => {
    const row = byId.get(id);
    if (!row) return;
    if (row.linked_transaction_id != null) ids.add(Number(row.linked_transaction_id));
    all.forEach((o) => {
      if (o.linked_transaction_id != null && Number(o.linked_transaction_id) === Number(id)) {
        ids.add(o.id);
      }
    });
  });

  if (await isOwnLedgerDualWriteEnabled()) {
    try {
      await dualWriteBatchDeleteLedgerTransactions([...ids]);
      return ids.size;
    } catch (err) {
      wrapDualWriteError(err);
    }
  }
  await wheelMutate("POST", "/transactions/batch-delete", { ids: [...ids] });
  return ids.size;
}

export async function deleteLedgerTransaction(txnId: number): Promise<number> {
  if (!(txnId > 0)) throw new WheelActionError("Missing transaction id.");
  const all = await fetchLedgerTransactions();
  const byId = new Map(all.map((txn) => [txn.id, txn]));
  const row = byId.get(txnId);
  if (!row) throw new WheelActionError("Transaction not found.");

  const ids = new Set<number>([txnId]);
  if (row.linked_transaction_id != null) ids.add(Number(row.linked_transaction_id));
  for (const other of all) {
    if (other.linked_transaction_id != null && Number(other.linked_transaction_id) === txnId) {
      ids.add(other.id);
    }
  }

  if (await isOwnLedgerDualWriteEnabled()) {
    try {
      await dualWriteBatchDeleteLedgerTransactions([...ids]);
      return ids.size;
    } catch (err) {
      wrapDualWriteError(err);
    }
  }
  await wheelMutate("POST", "/transactions/batch-delete", { ids: [...ids] });
  return ids.size;
}

export async function addRecommendationPosition(
  rec: WheelRecommendation,
  input: {
    tradeDate: string;
    quantity: number;
    avgPrice: number;
    strike?: number;
    expDate?: string;
  },
): Promise<void> {
  const ticker = rec.underlying_ticker;
  const side = rec.side || "put";
  const strike = input.strike ?? rec.strike;
  const expDate = input.expDate ?? rec.expiration_date;
  if (!ticker) throw new WheelActionError("Recommendation is missing ticker.");
  if (!(strike > 0)) throw new WheelActionError("Enter a valid strike.");
  if (!expDate) throw new WheelActionError("Choose an expiration date.");
  if (!(input.quantity > 0) || !(input.avgPrice >= 0)) {
    throw new WheelActionError("Enter a valid quantity and price.");
  }
  if (!input.tradeDate) throw new WheelActionError("Choose a trade date.");

  const optSym = buildOccSymbol(ticker, expDate, side, strike);
  const body = {
    instrument_type: "option",
    action: "sell",
    ticker,
    trade_date: input.tradeDate,
    quantity: input.quantity,
    avg_price: input.avgPrice,
    strike,
    side,
    exp_date: expDate,
    option_symbol: optSym,
    idempotency_key: ["add", optSym, "sell", input.tradeDate, input.quantity, input.avgPrice].join(
      ":",
    ),
  };
  await postLedgerTransaction(body);
}

export async function updateOpeningTransaction(
  position: OptionPosition,
  txn: WheelLedgerTransaction,
  input: {
    tradeDate: string;
    quantity: number;
    avgPrice: number;
    strike: number;
    expDate: string;
  },
): Promise<void> {
  await updateLedgerTransaction(txn, {
    tradeDate: input.tradeDate,
    quantity: input.quantity,
    avgPrice: input.avgPrice,
    strike: input.strike,
    expDate: input.expDate,
    ticker: position.ticker,
    side: position.side || txn.side,
    optionSymbol: position.option_symbol ?? txn.option_symbol ?? null,
  });
}

export interface UpdateLedgerTransactionInput {
  tradeDate: string;
  quantity: number;
  /** Option premium or stock price per share (Stock Forge ledger avg_price). */
  avgPrice: number;
  strike?: number;
  expDate?: string;
  ticker?: string;
  side?: string;
  optionSymbol?: string | null;
}

/** Update an existing ledger row via Stock Forge PUT /transactions/:id (or assignment-pair). */
export async function updateLedgerTransaction(
  txn: WheelLedgerTransaction,
  input: UpdateLedgerTransactionInput,
): Promise<void> {
  if (!(txn.id > 0)) throw new WheelActionError("Missing transaction id.");
  if (!(input.quantity > 0)) throw new WheelActionError("Enter a valid quantity.");
  if (!(input.avgPrice >= 0)) throw new WheelActionError("Enter a valid price.");
  if (!input.tradeDate) throw new WheelActionError("Choose a trade date.");

  const instrument = txn.instrument_type === "stock" ? "stock" : "option";
  const ticker = (input.ticker ?? txn.ticker ?? "").trim().toUpperCase();
  if (!ticker) throw new WheelActionError("Missing ticker.");

  const body: Record<string, unknown> = {
    instrument_type: instrument,
    action: txn.action,
    ticker,
    trade_date: input.tradeDate,
    quantity: input.quantity,
    avg_price: input.avgPrice,
  };

  if (instrument === "option") {
    const strike = input.strike ?? txn.strike;
    const expDate = (input.expDate ?? txn.exp_date ?? "").slice(0, 10);
    const side = input.side ?? txn.side ?? "put";
    if (!(Number(strike) > 0)) throw new WheelActionError("Enter a valid strike.");
    if (!expDate) throw new WheelActionError("Choose an expiration date.");
    body.side = side;
    body.strike = strike;
    body.exp_date = expDate;
    const optSym =
      input.optionSymbol ??
      buildOccSymbol(ticker, expDate, side, Number(strike)) ??
      txn.option_symbol;
    if (optSym) body.option_symbol = optSym;
    if (txn.close_reason) body.close_reason = txn.close_reason;
  }

  const all = await fetchTransactionsForEdits();
  const byId = new Map(all.map((t) => [t.id, t]));
  const editingRow = byId.get(txn.id);
  let linkedLeg: WheelLedgerTransaction | null = null;
  if (editingRow?.linked_transaction_id != null) {
    linkedLeg = byId.get(Number(editingRow.linked_transaction_id)) ?? null;
  }
  if (!linkedLeg) {
    for (const row of all) {
      if (row.linked_transaction_id != null && Number(row.linked_transaction_id) === txn.id) {
        linkedLeg = row;
        break;
      }
    }
  }

  const linkedAssignmentStock =
    linkedLeg?.instrument_type === "stock" ? linkedLeg : null;

  if (linkedAssignmentStock && instrument === "option") {
    const pairBody = {
      option_transaction_id: txn.id,
      option: body,
    };
    if (await isOwnLedgerDualWriteEnabled()) {
      try {
        await dualWriteAssignmentPairLedger(pairBody);
        return;
      } catch (err) {
        wrapDualWriteError(err);
      }
    }
    await wheelMutate("PUT", "/transactions/assignment-pair", pairBody);
  } else {
    if (await isOwnLedgerDualWriteEnabled()) {
      try {
        await dualWriteUpdateLedgerTransactionByOwnId(txn.id, body);
        return;
      } catch (err) {
        wrapDualWriteError(err);
      }
    }
    await wheelMutate("PUT", `/transactions/${txn.id}`, body);
  }
}

export type LedgerTransactionKind =
  | "sto"
  | "btc"
  | "bto"
  | "stc"
  | "exp"
  | "asgn"
  | "stock_buy"
  | "stock_sell";

export interface CreateLedgerTransactionInput {
  kind: LedgerTransactionKind;
  ticker: string;
  tradeDate: string;
  quantity: number;
  /** Option premium or stock price per share (Stock Forge ledger avg_price). */
  avgPrice?: number;
  side?: "put" | "call";
  strike?: number;
  expDate?: string;
  /** Own-ledger broker account this manual trade belongs to. */
  accountId?: string;
}

/** Keep Stock Forge idempotency keys ≤64 chars while still distinguishing accounts. */
function accountIdempotencySuffix(accountId?: string): string {
  const id = String(accountId ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  return id ? id.slice(-10) : "";
}

function withAccountFields(
  body: Record<string, unknown>,
  accountId?: string,
): Record<string, unknown> {
  const id = String(accountId ?? "").trim();
  if (!id) return body;
  const suffix = accountIdempotencySuffix(id);
  const key = String(body.idempotency_key ?? "");
  return {
    ...body,
    account_id: id,
    ...(suffix && key ? { idempotency_key: `${key}:${suffix}`.slice(0, 64) } : {}),
  };
}

function buildCreateLedgerBody(input: CreateLedgerTransactionInput): Record<string, unknown> {
  const ticker = input.ticker.trim().toUpperCase();
  const tradeDate = input.tradeDate;
  const quantity = input.quantity;

  if (input.kind === "stock_buy" || input.kind === "stock_sell") {
    return withAccountFields(
      {
        instrument_type: "stock",
        action: input.kind === "stock_buy" ? "buy" : "sell",
        ticker,
        trade_date: tradeDate,
        quantity,
        avg_price: input.avgPrice,
        idempotency_key: ["manual-add", input.kind, ticker, tradeDate, quantity, input.avgPrice].join(
          ":",
        ),
      },
      input.accountId,
    );
  }

  const side = input.side === "call" ? "call" : "put";
  const strike = Number(input.strike);
  const expDate = (input.expDate ?? "").slice(0, 10);
  const optionSymbol = buildOccSymbol(ticker, expDate, side, strike);
  const base: Record<string, unknown> = {
    instrument_type: "option",
    ticker,
    trade_date: tradeDate,
    quantity,
    side,
    strike,
    exp_date: expDate,
  };
  if (optionSymbol) base.option_symbol = optionSymbol;

  switch (input.kind) {
    case "sto":
      return withAccountFields(
        {
          ...base,
          action: "sell",
          avg_price: input.avgPrice,
          option_trans_code: "STO",
          idempotency_key: ["manual-add", "sto", optionSymbol, tradeDate, quantity, input.avgPrice].join(
            ":",
          ),
        },
        input.accountId,
      );
    case "btc":
      return withAccountFields(
        {
          ...base,
          action: "buy",
          avg_price: input.avgPrice,
          option_trans_code: "BTC",
          idempotency_key: ["manual-add", "btc", optionSymbol, tradeDate, quantity, input.avgPrice].join(
            ":",
          ),
        },
        input.accountId,
      );
    case "bto":
      return withAccountFields(
        {
          ...base,
          action: "buy",
          avg_price: input.avgPrice,
          option_trans_code: "BTO",
          idempotency_key: ["manual-add", "bto", optionSymbol, tradeDate, quantity, input.avgPrice].join(
            ":",
          ),
        },
        input.accountId,
      );
    case "stc":
      return withAccountFields(
        {
          ...base,
          action: "sell",
          avg_price: input.avgPrice,
          option_trans_code: "STC",
          idempotency_key: ["manual-add", "stc", optionSymbol, tradeDate, quantity, input.avgPrice].join(
            ":",
          ),
        },
        input.accountId,
      );
    case "exp":
      return withAccountFields(
        {
          ...base,
          action: "buy",
          avg_price: 0,
          close_reason: "expired",
          option_trans_code: "OEXP",
          idempotency_key: ["manual-add", "exp", optionSymbol, tradeDate, quantity].join(":"),
        },
        input.accountId,
      );
    case "asgn":
      return withAccountFields(
        {
          ...base,
          action: "buy",
          avg_price: strike,
          close_reason: "assigned",
          option_trans_code: "OASGN",
          idempotency_key: ["manual-add", "asgn", optionSymbol, tradeDate, quantity, strike].join(":"),
        },
        input.accountId,
      );
    default:
      throw new WheelActionError("Unsupported transaction type.");
  }
}

/** Create a ledger row via Stock Forge POST /transactions. */
export async function createLedgerTransaction(input: CreateLedgerTransactionInput): Promise<void> {
  const ticker = input.ticker.trim().toUpperCase();
  if (!ticker) throw new WheelActionError("Enter a ticker.");
  if (!input.tradeDate) throw new WheelActionError("Choose a trade date.");
  if (!(input.quantity > 0)) throw new WheelActionError("Enter a valid quantity.");
  if (!String(input.accountId ?? "").trim()) {
    throw new WheelActionError("Choose a broker account.");
  }

  const isStock = input.kind === "stock_buy" || input.kind === "stock_sell";
  if (isStock) {
    if (input.avgPrice == null || !(input.avgPrice >= 0)) {
      throw new WheelActionError("Enter a valid price.");
    }
  } else {
    const strike = Number(input.strike);
    const expDate = (input.expDate ?? "").slice(0, 10);
    if (!(strike > 0)) throw new WheelActionError("Enter a valid strike.");
    if (!expDate) throw new WheelActionError("Choose an expiration date.");
    if (!buildOccSymbol(ticker, expDate, input.side ?? "put", strike)) {
      throw new WheelActionError("Could not build option symbol from contract details.");
    }
    if (input.kind !== "exp" && input.kind !== "asgn") {
      if (input.avgPrice == null || !(input.avgPrice >= 0)) {
        throw new WheelActionError("Enter a valid price.");
      }
    }
  }

  await postLedgerTransaction(buildCreateLedgerBody(input));
}

export function deleteConfirmMessage(position: OptionPosition, txnCount: number): string {
  const linkedNote =
    txnCount > 1
      ? `\n\nThis removes every ledger entry for this option (opens, closes, adjustments) and any linked assignment stock legs.`
      : `\n\nThis removes every ledger entry for this option.`;
  return (
    `Delete all ${txnCount} transaction(s) for ${positionSummary(position)}?\n\n` +
    `Contract: ${position.option_symbol ?? "—"}${linkedNote}\n\nThis cannot be undone.`
  );
}

export async function countPositionTransactions(position: OptionPosition): Promise<number> {
  if (!position.option_symbol) return 0;
  const all = await fetchLedgerTransactions();
  return all.filter((t) => t.option_symbol === position.option_symbol).length;
}

type WheelLedgerRefreshFn = () => void | Promise<void>;

let wheelLedgerRefreshFn: WheelLedgerRefreshFn | null = null;

/** Register a host-app callback to reload the active wheel tab after ledger writes. */
export function setWheelLedgerRefresh(fn: WheelLedgerRefreshFn | null): void {
  wheelLedgerRefreshFn = fn;
}

export async function afterWheelLedgerChange(onRefresh?: WheelLedgerRefreshFn): Promise<void> {
  invalidateWheelSnapshots();
  notifyNativeWheelRefresh();
  const refresh = onRefresh ?? wheelLedgerRefreshFn;
  try {
    await refresh?.();
  } catch {
    /* Host tab refresh is best-effort — ledger write already succeeded. */
  }
}

export interface AgenticLedgerUpdateInput {
  kind: "sto" | "btc" | "exp" | "asgn";
  ticker: string;
  side: "put" | "call" | string;
  strike: number;
  expDate: string;
  quantity: number;
  optionSymbol?: string | null;
  avgPrice?: number | null;
  tradeDate?: string;
}

function resolutionForKind(kind: AgenticLedgerUpdateInput["kind"]): PositionResolution | null {
  if (kind === "btc") return "closed";
  if (kind === "exp") return "expired";
  if (kind === "asgn") return "assigned";
  return null;
}

function txnMatchesAgenticKind(
  txn: WheelLedgerTransaction,
  kind: AgenticLedgerUpdateInput["kind"],
): boolean {
  if (kind === "sto") {
    return txn.instrument_type === "option" && txn.action === "sell" && !txn.close_reason;
  }
  if (kind === "btc") {
    return txn.instrument_type === "option" && txn.action === "buy" && !txn.close_reason;
  }
  if (kind === "exp") {
    return txn.close_reason === "expired";
  }
  if (kind === "asgn") {
    return txn.close_reason === "assigned";
  }
  return false;
}

export function findLedgerTxnIdsInTransactions(
  update: AgenticLedgerUpdateInput,
  transactions: WheelLedgerTransaction[],
  tradeDate?: string,
): number[] {
  const optionSymbol =
    update.optionSymbol ??
    buildOccSymbol(update.ticker, update.expDate, update.side, update.strike);
  if (!optionSymbol) return [];
  const matches = transactions.filter((txn) => {
    if (txn.option_symbol !== optionSymbol) return false;
    if (!txnMatchesAgenticKind(txn, update.kind)) return false;
    if (tradeDate && txn.trade_date !== tradeDate) return false;
    return true;
  });
  matches.sort((a, b) => Number(b.id) - Number(a.id));
  return matches.map((txn) => txn.id);
}

export async function findLedgerTxnIdsForAgenticUpdate(
  update: AgenticLedgerUpdateInput,
  tradeDate?: string,
): Promise<number[]> {
  const optionSymbol =
    update.optionSymbol ??
    buildOccSymbol(update.ticker, update.expDate, update.side, update.strike);
  if (!optionSymbol) return [];
  try {
    const all = await fetchAllLedgerTransactions();
    return findLedgerTxnIdsInTransactions(update, all, tradeDate);
  } catch {
    return [];
  }
}

export async function applyAgenticUpdateToLedger(
  update: AgenticLedgerUpdateInput,
  input: { tradeDate: string; avgPrice?: number },
): Promise<number[]> {
  const ticker = update.ticker.trim().toUpperCase();
  const side = update.side === "call" ? "call" : "put";
  const strike = Number(update.strike);
  const expDate = update.expDate.slice(0, 10);
  const quantity = Number(update.quantity);
  const tradeDate = input.tradeDate;
  if (!ticker || !(strike > 0) || !expDate || !(quantity > 0) || !tradeDate) {
    throw new WheelActionError("Update is missing contract details.");
  }

  const optionSymbol =
    update.optionSymbol ?? buildOccSymbol(ticker, expDate, side, strike);
  if (!optionSymbol) throw new WheelActionError("Could not build option symbol.");

  if (update.kind === "sto") {
    const rawAvg = input.avgPrice ?? update.avgPrice ?? 0;
    const avgPrice = roundOptionPremium(
      rawAvg < 0 ? (normalizeRhOptionPremium(rawAvg) ?? 0) : Number(rawAvg),
    );
    if (!(avgPrice >= 0)) throw new WheelActionError("Enter a valid premium.");
    await postLedgerTransaction({
      instrument_type: "option",
      action: "sell",
      ticker,
      trade_date: tradeDate,
      quantity,
      avg_price: avgPrice,
      strike,
      side,
      exp_date: expDate,
      option_symbol: optionSymbol,
      idempotency_key: ["agentic-add", optionSymbol, "sell", tradeDate, quantity, avgPrice].join(":"),
    });
  } else {
    const resolution = resolutionForKind(update.kind);
    if (!resolution) throw new WheelActionError("Unsupported update kind.");
    const positions = await wheelGet<{ positions?: OptionPosition[] }>("/positions");
    const open = positions.positions?.find((row) => row.option_symbol === optionSymbol) ?? null;
    if (open) {
      const rawAvg = input.avgPrice ?? update.avgPrice ?? 0;
      const avgPrice =
        resolution === "closed"
          ? roundOptionPremium(
              rawAvg < 0 ? (normalizeRhOptionPremium(rawAvg) ?? 0) : Number(rawAvg),
            )
          : resolution === "assigned"
            ? strike
            : undefined;
      if (resolution === "closed" && !(Number(avgPrice) >= 0)) {
        throw new WheelActionError("Enter the buy-to-close premium per share.");
      }
      await resolveOpenPosition(open, resolution, {
        tradeDate,
        quantity,
        avgPrice: avgPrice != null ? Number(avgPrice) : undefined,
      });
    } else {
      const body: Record<string, unknown> = {
        instrument_type: "option",
        action: "buy",
        ticker,
        trade_date: tradeDate,
        quantity,
        strike,
        side,
        exp_date: expDate,
        option_symbol: optionSymbol,
        idempotency_key: ["agentic-close", optionSymbol, update.kind, tradeDate, quantity].join(":"),
      };
      if (update.kind === "exp") {
        body.close_reason = "expired";
        body.avg_price = 0;
      } else if (update.kind === "asgn") {
        body.close_reason = "assigned";
        body.avg_price = strike;
      } else {
        const avgPrice = input.avgPrice ?? update.avgPrice ?? 0;
        if (!(avgPrice >= 0)) throw new WheelActionError("Enter the buy-to-close premium per share.");
        body.avg_price = avgPrice;
      }
      await postLedgerTransaction(body);
    }
  }

  const ids = await findLedgerTxnIdsForAgenticUpdate(
    { ...update, optionSymbol, tradeDate },
    tradeDate,
  );
  return ids;
}

export { etTodayISO };

export function readExpDateField(input: HTMLInputElement): string {
  const normalized = normalizeDateInput(input.value);
  if (!normalized) {
    throw new WheelActionError("Choose an expiration date.");
  }
  return normalized;
}
